/**
 * Candidate Dashboard (Career Hub)
 * Track applications, saved jobs, career services used, and subscription
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Briefcase,
  FileText,
  Heart,
  Clock,
  CheckCircle2,
  XCircle,
  Calendar,
  TrendingUp,
  Star,
  Eye,
  Users,
  Zap,
  ChevronRight,
  Loader2,
  Search,
  Settings,
  Crown,
  Target,
  Award,
  BarChart3,
  MessageSquare,
  Building2,
  MapPin,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Application Status Colors
const STATUS_COLORS = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock },
  viewed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Eye },
  shortlisted: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Star },
  interview: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Calendar },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  offer: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, change, changeType = 'neutral' }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="p-2 rounded-lg bg-blue-50">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        {change && (
          <Badge variant={changeType === 'up' ? 'default' : changeType === 'down' ? 'destructive' : 'secondary'} className="text-xs">
            {changeType === 'up' ? '+' : ''}{change}
          </Badge>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

// Application Card Component
const ApplicationCard = ({ application, onView }) => {
  const status = STATUS_COLORS[application.status] || STATUS_COLORS.pending;
  const StatusIcon = status.icon;
  const job = application.job || {};

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onView}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 truncate">{job.title || 'Job Title'}</h4>
            <p className="text-sm text-gray-500">{job.company_name || 'Company'}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
              <MapPin className="h-3 w-3" />
              <span>{job.location || 'Location'}</span>
            </div>
          </div>
          <Badge className={`${status.bg} ${status.text} flex items-center gap-1`}>
            <StatusIcon className="h-3 w-3" />
            {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
          </Badge>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-gray-400">
          <span>Applied {new Date(application.created_at).toLocaleDateString()}</span>
          <ChevronRight className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
};

// Saved Job Card Component
const SavedJobCard = ({ savedJob, onView, onRemove }) => {
  const job = savedJob.job || {};

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onView}>
            <h4 className="font-semibold text-gray-900 truncate">{job.title || 'Job Title'}</h4>
            <p className="text-sm text-gray-500">{job.company_name || 'Company'}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {job.location || 'Location'}
              </span>
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {job.employment_type || 'Full-time'}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
          </Button>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <span className="text-sm font-semibold text-green-600">
            {job.salary_min && job.salary_max 
              ? `${job.currency_symbol || 'Rs'}${job.salary_min?.toLocaleString()} - ${job.salary_max?.toLocaleString()}`
              : 'Negotiable'
            }
          </span>
          <Button size="sm" onClick={onView}>
            View Job
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
const CandidateDashboard = () => {
  const navigate = useNavigate();
  const { country, formatCurrency } = useCountry();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAuthenticated = !!localStorage.getItem('token');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated]);

  const fetchDashboardData = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [dashboardRes, appsRes, savedRes] = await Promise.all([
        axios.get(`${API_URL}/api/job-dashboard/candidate/dashboard/${user.id}?country=${country}`),
        axios.get(`${API_URL}/api/job-dashboard/candidate/applications/${user.id}`),
        axios.get(`${API_URL}/api/job-dashboard/candidate/saved-jobs/${user.id}?country=${country}`),
      ]);
      
      if (dashboardRes.data.success) {
        setDashboardData(dashboardRes.data);
      }
      if (appsRes.data.success) {
        setApplications(appsRes.data.applications || []);
      }
      if (savedRes.data.success) {
        setSavedJobs(savedRes.data.saved_jobs || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSavedJob = async (jobId) => {
    try {
      await axios.delete(`${API_URL}/api/job-dashboard/candidate/saved-job/${jobId}?user_id=${user.id}`);
      setSavedJobs(prev => prev.filter(j => j.job_id !== jobId));
      toast({
        title: 'Job removed',
        description: 'Job removed from saved list',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove job',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const statusBreakdown = dashboardData?.status_breakdown || {};
  const subscription = dashboardData?.subscription;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="candidate-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-blue-200 text-sm mb-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/jobs" className="hover:text-white">Jobs</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Career Hub</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                <Target className="inline h-8 w-8 mr-2" />
                Career Hub
              </h1>
              <p className="text-blue-100">
                Track your job applications and career progress
              </p>
            </div>
            
            {subscription ? (
              <Badge className="bg-amber-500 text-white px-4 py-2 text-sm">
                <Crown className="h-4 w-4 mr-2" />
                {subscription.plan_name}
              </Badge>
            ) : (
              <Button variant="secondary" onClick={() => navigate('/jobs/subscription')}>
                Upgrade to Pro
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatsCard
            icon={FileText}
            label="Applications"
            value={stats.applications_sent || 0}
          />
          <StatsCard
            icon={Calendar}
            label="Interviews"
            value={stats.interviews_scheduled || 0}
            change={stats.interviews_scheduled > 0 ? `${stats.interviews_scheduled} scheduled` : null}
            changeType="up"
          />
          <StatsCard
            icon={Heart}
            label="Saved Jobs"
            value={stats.saved_jobs || 0}
          />
          <StatsCard
            icon={Eye}
            label="Profile Views"
            value={stats.profile_views || 0}
          />
          <StatsCard
            icon={Award}
            label="Services Used"
            value={stats.services_used || 0}
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="applications">Applications ({applications.length})</TabsTrigger>
            <TabsTrigger value="saved">Saved Jobs ({savedJobs.length})</TabsTrigger>
            <TabsTrigger value="services">Career Services</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Application Pipeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                  Application Pipeline
                </CardTitle>
                <CardDescription>Track your applications through the hiring process</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                  {Object.entries(statusBreakdown).map(([status, count]) => {
                    const statusConfig = STATUS_COLORS[status] || STATUS_COLORS.pending;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <div key={status} className="text-center p-3 rounded-lg bg-gray-50">
                        <div className={`inline-flex p-2 rounded-full ${statusConfig.bg} mb-2`}>
                          <StatusIcon className={`h-4 w-4 ${statusConfig.text}`} />
                        </div>
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-xs text-gray-500 capitalize">{status}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Applications */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Recent Applications</CardTitle>
                  <CardDescription>Your latest job applications</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('applications')}>
                  View All
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {applications.slice(0, 5).map((app) => (
                    <ApplicationCard
                      key={app.id}
                      application={app}
                      onView={() => navigate(`/jobs/${app.job_id}`)}
                    />
                  ))}
                  {applications.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No applications yet</p>
                      <Button variant="link" onClick={() => navigate('/jobs')}>
                        Browse Jobs
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card>
              <CardHeader>
                <CardTitle>All Applications</CardTitle>
                <CardDescription>Track all your job applications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {applications.map((app) => (
                    <ApplicationCard
                      key={app.id}
                      application={app}
                      onView={() => navigate(`/jobs/${app.job_id}`)}
                    />
                  ))}
                  {applications.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No applications yet</p>
                      <Button variant="link" onClick={() => navigate('/jobs')}>
                        Start applying to jobs
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Saved Jobs Tab */}
          <TabsContent value="saved">
            <Card>
              <CardHeader>
                <CardTitle>Saved Jobs</CardTitle>
                <CardDescription>Jobs you've saved for later</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedJobs.map((saved) => (
                    <SavedJobCard
                      key={saved.id}
                      savedJob={saved}
                      onView={() => navigate(`/jobs/${saved.job_id}`)}
                      onRemove={() => handleRemoveSavedJob(saved.job_id)}
                    />
                  ))}
                  {savedJobs.length === 0 && (
                    <div className="col-span-2 text-center py-8 text-gray-500">
                      <Heart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No saved jobs</p>
                      <Button variant="link" onClick={() => navigate('/jobs')}>
                        Browse jobs to save
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Career Services Tab */}
          <TabsContent value="services">
            <Card>
              <CardHeader>
                <CardTitle>Career Services</CardTitle>
                <CardDescription>Boost your job search with professional services</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <FileText className="h-8 w-8 text-blue-600 mb-3" />
                      <h4 className="font-semibold mb-1">CV Review</h4>
                      <p className="text-sm text-gray-500 mb-3">Get expert feedback on your CV</p>
                      <Button size="sm" className="w-full" onClick={() => navigate('/jobs?tab=services')}>
                        Get Started
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="p-4">
                      <MessageSquare className="h-8 w-8 text-purple-600 mb-3" />
                      <h4 className="font-semibold mb-1">Interview Prep</h4>
                      <p className="text-sm text-gray-500 mb-3">Practice with mock interviews</p>
                      <Button size="sm" className="w-full" onClick={() => navigate('/jobs?tab=services')}>
                        Book Session
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="p-4">
                      <TrendingUp className="h-8 w-8 text-emerald-600 mb-3" />
                      <h4 className="font-semibold mb-1">Career Coaching</h4>
                      <p className="text-sm text-gray-500 mb-3">1-on-1 career guidance</p>
                      <Button size="sm" className="w-full" onClick={() => navigate('/jobs?tab=services')}>
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CandidateDashboard;
